import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import SEO from '../components/SEO';
import { ArrowRight } from 'lucide-react';
import { useSiteData } from '../lib/SiteDataContext';
import { resolveImage } from '../lib/api';

const GovernmentPage = () => {
  const { slug } = useParams();
  const { settings } = useSiteData();
  const links = (settings?.government_links || []).slice().sort((a, b) => a.order - b.order);
  const item = links.find((g) => g.slug === slug) || links[0];
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);
  if (!item) {
    return (
      <section className="py-24 text-center text-[#0B2545]">
        <h2 className="font-serif text-2xl font-bold">Page not found</h2>
        <p className="mt-2 text-[#0B2545]/60">Configure Government links in Site Settings.</p>
      </section>
    );
  }
  const isPresident = item.slug === 'office-of-the-president';
  const p = settings?.president || {};
  const photo = p.photo_url ? resolveImage(p.photo_url) : '';

  return (
    <>
      <SEO
        title={item.name}
        description={
          (item.body_paragraphs && item.body_paragraphs[0]) ||
          (isPresident ? p.headline : `${item.name} of the South East State of Somalia.`)
        }
        image={isPresident && photo ? photo : undefined}
        keywords={`${item.name}, Somalia government, South East State, ${isPresident ? p.name + ', President of South East State' : ''}`}
      />
      <PageHeader title={item.name} crumbs={[{ label: 'Government' }, { label: item.name }]} />
      <section className="bg-white py-14">
        <div className="mx-auto grid max-w-[1280px] grid-cols-1 gap-10 px-6 lg:grid-cols-12">
          {isPresident ? (
            <>
              <div className="lg:col-span-4">
                <div className="overflow-hidden rounded-lg border border-[#E6DDC8] bg-white shadow-md">
                  {photo ? (
                    <img src={photo} alt={p.name} className="h-[420px] w-full object-cover" />
                  ) : (
                    <div className="flex h-[420px] w-full items-center justify-center bg-[#F6F2EA] text-[#0B2545]/40">Photo not set</div>
                  )}
                  <div className="border-t-2 border-[#C9A86A] p-5">
                    <div className="font-serif text-lg font-bold text-[#0B2545]">{p.name}</div>
                    <div className="text-[13px] text-[#0B2545]/65">{p.title}</div>
                  </div>
                </div>
              </div>
              <div className="lg:col-span-8">
                <div className="mb-3 text-[12px] font-semibold uppercase tracking-[0.22em] text-[#C9A86A]">Full Message from the President</div>
                <h2 className="font-serif text-3xl font-bold leading-tight text-[#0B2545] md:text-[36px]">{p.headline}</h2>
                <div className="mt-5 h-[3px] w-16 bg-[#C9A86A]" />
                <div className="mt-7 space-y-5 text-[15px] leading-relaxed text-[#0B2545]/80">
                  {(p.paragraphs || []).map((para, i) => <p key={i}>{para}</p>)}
                  {(item.body_paragraphs || []).map((para, i) => <p key={`b${i}`}>{para}</p>)}
                </div>
              </div>
            </>
          ) : (
            <div className="lg:col-span-12">
              <div className="mb-3 text-[12px] font-semibold uppercase tracking-[0.22em] text-[#C9A86A]">Government Institution</div>
              <h2 className="font-serif text-3xl font-bold text-[#0B2545] md:text-[36px]">{item.name}</h2>
              <div className="mt-5 h-[3px] w-16 bg-[#C9A86A]" />
              <div className="mt-7 max-w-3xl space-y-5 text-[15px] leading-relaxed text-[#0B2545]/80">
                {(item.body_paragraphs || []).map((para, i) => <p key={i}>{para}</p>)}
              </div>
              <Link to="/contact" className="group mt-8 inline-flex items-center gap-2 rounded-md bg-[#0B2545] px-5 py-3 text-[14px] font-medium text-white hover:bg-[#08203a]">
                Get in Touch
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default GovernmentPage;
